import * as loadjs from 'loadjs'
import { LoadJsBundle } from '../enums/LoadJsBundle'
import GoogletagFacade from '../googletagFacade/GoogletagFacade'
import { domReady, escapeHtml } from '../helpers/DOM'
import { queryParamHas, getQueryParam } from '../helpers/getQueryParam'
import Logger from '../helpers/Logger'
import WebModel from '../models/WebModel'

export const CMP_SCRIPT_URL = `//cmp.mediavine.com/cmp-${window.$mediavine.web.cmp_version}.js`
export const CMP_BUNDLE_NAME = LoadJsBundle.cmp
const _isGdprTest = queryParamHas('test', 'gdpr')

export default class CMP {
  commandQueue: Array<object> = []
  config: ICmpConfig
  cmp: ICmpFunction
  consentData

  /**
   * Depending on whether we the user is in an EU country, and the settings for
   * a particular website this module will handle GDPR rules specifying what
   * kind of ads can be served.
   *
   * @param model WebModel
   * @param IS_EU  Are we in an EU country
   * @param enableCmp Should we enable the CMP
   * @param _window
   */
  constructor(
    private model: WebModel,
    private IS_EU: boolean = model.gdpr === '1',
    public enableCmp: boolean = (IS_EU && model.gdpr_compliance === 1) ||
      _isGdprTest,
    private _window: Window = window
  ) {
    if (this._window.__cmp) {
      Logger.debug('window.__cmp found.')
      loadjs.done(LoadJsBundle.gdpr)

      return
    }
    const disableCmp = getQueryParam('disableCmp', _window.location.search)
    if (disableCmp !== undefined) {
      this.enableCmp = false
    }

    const listen = this._window.attachEvent || this._window.addEventListener

    if (this.enableCmp) {
      this.cmp = this.cmpIfEnabled.bind(this)
      this.cmp.config = this.setupCmpConfig()
      this.cmp.commandQueue = this.commandQueue
      this.cmp.receiveMessage = this.receiveMessage
      this._window.__cmp = this.cmp

      listen('message', this.receiveMessage.bind(this), false)

      this.addLocatorFrame()

      loadjs(CMP_SCRIPT_URL, CMP_BUNDLE_NAME)
      loadjs.ready(CMP_BUNDLE_NAME, this.setupCmp.bind(this))

      // Insert Link in Footer
      domReady(this.insertPrivacySettingElement.bind(this))
    } else {
      this.cmp = this.cmpIfDisabled.bind(this)
      this._window.__cmp = this.cmp

      // for framed scenarios
      listen('message', this.receiveCmpDisabledMessage.bind(this))

      if (
        this.IS_EU &&
        (!model.gdpr_compliance || model.gdpr_compliance === 0)
      ) {
        // Disable all Personalized Ads
        GoogletagFacade.onReady(() => {
          this._window.googletag.pubads().setRequestNonPersonalizedAds(1)
        }).catch()
      }

      loadjs.done('gdpr')
    }
  }

  // This sets up a shell so that when the actual CMP loads, it can
  // execute the commands in the commandQueue (I THINK!?!?!)
  private cmpIfEnabled(command, parameter, callback): void {
    if (command === 'ping') {
      if (callback) {
        callback({
          gdprAppliesGlobally: !!(
            this._window.__cmp.config &&
            this._window.__cmp.config.storeConsentGlobally
          ),
          cmpLoaded: false
        })
      }
    } else {
      this.commandQueue.push({
        command,
        parameter,
        callback
      })
    }
  }

  // How to handle calls to window.__cmp if enableCmp === false
  private cmpIfDisabled(command, version, callback): void {
    const iabConsentData = ''
    const gdprApplies = this.IS_EU
    const responseCode = true
    if (command === 'getConsentData') {
      callback({ consentData: iabConsentData, gdprApplies }, responseCode)
    } else if (command === 'getVendorConsents') {
      callback({ metadata: iabConsentData, gdprApplies }, responseCode)
    } else {
      callback(undefined, false)
    }
  }

  /**
   * Helper function to set up the CMP config object based on the web model setting
   */
  private setupCmpConfig(): ICmpConfig {
    this.config = {
      globalVendorListLocation: `${this.model.launcherUrl}/cmp/vendorlist.json`,
      // Scope opt-in to the domain level
      storeConsentGlobally: false,
      // Only enable GDPR if CDN detects user (even in test mode)
      gdprApplies: this.model.gdpr === '1',
      siteTitle: escapeHtml(this.model.title),
      privacyLink: escapeHtml(this.model.privacy_policy_link),
      customPurposes: this.model.customPurposes,
      vendorList: this.model.vendorList

      // globalConsentLocation: './portal.html',
      // logging: 'debug',
    }

    if (this.model.customPurposes.length > 0) {
      this.config.storePublisherData = true
    }

    if (this.model.custom_cmp_header) {
      this.config.customTitle = escapeHtml(this.model.custom_cmp_header)
    }

    if (this.model.custom_cmp_color) {
      this.config.customBrandColor = escapeHtml(this.model.custom_cmp_color)
    }

    return this.config
  }

  // Sets up an iframe named __cmpLocator
  // Why: https://bit.ly/2JU5R5z
  private addLocatorFrame(): void {
    if (!this._window.frames['__cmpLocator']) {
      if (document.body) {
        const frame = document.createElement('iframe')
        frame.style.display = 'none'
        frame.name = '__cmpLocator'
        document.body.appendChild(frame)
      } else {
        const waitMs = 5
        setTimeout(this.addLocatorFrame, waitMs)
      }
    }
  }

  private getVendorConsentsCallback(result, onSubmit?): void {
    // If they don't allow personalization and Google, show non-personalized ads at Google
    /* eslint-disable-next-line no-magic-numbers */
    if (!(result.vendorConsents[5] && result.purposeConsents[2])) {
      GoogletagFacade.onReady<void>(() => {
        this._window.googletag.pubads().setRequestNonPersonalizedAds(1)
        // TODO: Set DFP key value as well
      }).catch((e) => Logger.debug('Error setting non-personalized ads!', e))
    }

    this.consentData = result.metadata

    if (window.newrelic) {
      window.newrelic.addPageAction('gdpr', {
        domain: window.location.hostname.replace('www.', ''),
        popup: onSubmit ? 1 : 0,
        /* eslint-disable-next-line no-magic-numbers */
        consent: result.vendorConsents[5] && result.purposeConsents[2] ? 1 : 0,
        metadata: result.metadata
      })
    }

    // Ready to load ads
    loadjs.done('gdpr')
  }

  // Inserts link at bottom of (footer_selector || 'body') for user to
  // update their Privacy settings
  private insertPrivacySettingElement(): void {
    const selector = document.querySelector(
      this.model.footer_selector ? this.model.footer_selector : 'body'
    )
    if (selector) {
      selector.insertAdjacentHTML(
        'beforeend',
        `<div id='mv_privacy_settings' style='text-align : center; font-size : 11px; margin-top : 10px;'>
          <a href="javascript:window.__cmp('showConsentTool')">Update Privacy Settings</a>
        </div>`
      )
    }
  }

  /**
   * Helper function that sets up various __cmp listeners and actions.
   */
  private setupCmp(): void {
    this._window.__cmp('addEventListener', 'cmpReady', (result) => {
      Logger.debug(`Event "cmpReady" called: ${JSON.stringify(result)}`)
      this._window.__cmp('getVendorConsents', '', (resultB) => {
        Logger.debug(`cmpReady getVendorConsents: ${JSON.stringify(resultB)}`)
        this.getVendorConsentsCallback(resultB, false)
      })

      this._window.__cmp('getConsentData', '', (resultB) => {
        Logger.debug(`cmpReady getConsentData: ${JSON.stringify(resultB)}`)
      })
    })

    this._window.__cmp('addEventListener', 'onSubmit', (result) => {
      Logger.debug(`Event "onSubmit" called: ${JSON.stringify(result)}`)

      this._window.__cmp('getVendorConsents', '', (resultB) => {
        Logger.debug(`onSubmit getVendorConsents: ${JSON.stringify(resultB)}`)
        this.getVendorConsentsCallback(resultB, true)
      })
    })

    // If gdpr test, always force consent
    if (_isGdprTest) {
      this._window.__cmp('showConsentTool')
    }
  }

  /**
   * Helper function to handle CMP message event.
   * @param event
   */
  private receiveMessage(event): void {
    const data = event && event.data && event.data.__cmpCall
    if (data) {
      this.commandQueue.push({
        callId: data.callId,
        command: data.command,
        parameter: data.parameter,
        event
      })
    }
  }

  private receiveCmpDisabledMessage(event): void {
    try {
      const call = event.data.__cmpCall
      if (call) {
        this._window.__cmp(
          call.command,
          call.parameter,
          (retValue, success) => {
            const returnMsg = {
              __cmpReturn: {
                returnValue: retValue,
                success,
                callId: call.callId
              }
            }
            if (event.source) {
              event.source.postMessage(returnMsg, '*')
            }
          }
        )
      }
    } catch (e) {
      // do nothing
    }
  }
}

declare global {
  namespace googletag {
    interface PubAdsService {
      setRequestNonPersonalizedAds(nonPersonalizedAds: 1 | 0): PubAdsService
    }
  }

  interface ICmpFunction extends Function {
    config?: ICmpConfig
    commandQueue?: Array<object>
    receiveMessage?: Function
  }

  interface ICmpConfig {
    globalVendorListLocation: string
    storeConsentGlobally: boolean
    gdprApplies: boolean
    siteTitle: string
    privacyLink: string

    storePublisherData?: boolean
    customPurposeListLocation?: string
    customTitle?: string
    customBrandColor?: string
    customPurposes: Array<{ id: number; name: string; description: string }>
    vendorList: Array<object>
  }
}
